<template>
  <div class="container-fluid">
    <div class="no-print">
      <h3> Buy Sheet ({{ generatedAt }})
        <b-button v-b-modal.modal-1 size="sm" variant="link">
          <i class="fas fa-question-circle"></i>
        </b-button>
      </h3>
      <div>
        <b-modal id="modal-1" title="More Info" size="lg">
          <strong>How to use this page:</strong><br />
          This page shows all inventory items with available quantities below their set stock alarms. If the items are
          ordered as something else, those items are listed below in a sub-table.<br />
          <br />
          <strong>Highlighting Help:</strong><br />
          <strong>Part Number:</strong> Part numbers are highlighted yellow if they are marked as 'Purchased' under
          'Report Type' in fishbowl, which causes them to appear on this sheet. (it does not matter if they are actually
          purchased or not)<br />
          <strong>Total On Order:</strong> Totals are highlighted red if there are none of this item on order<br />
          <strong>Available:</strong> Totals are highlighted red if there are none of this item available<br />
          <br />
          <strong>Sub-Tables:</strong><br />
          The items in these tables are from Fishbowl's "Ordered as" custom fields<br />
          <strong>Available:</strong> If the item is an inventory item, it shows how many are currently available,
          otherwise it shows the part type<br />
        </b-modal>
      </div>

      <div>
        <b-form inline class="justify-content-center">
          <label for="show-po-button">Show items On Order</label>
          <b-form-checkbox id="show-po-button" class="ml-2" v-model="showPos" @input="getData" />
          <b-form-input @input="filterItems" v-model="filter" placeholder="Type to Filter"
            style="width: 50%; margin: 5px;" class="mx-1" />
          <b-btn @click="() => filter = ''" variant="danger" class="mx-1">Clear Filter</b-btn>
        </b-form>
      </div>
    </div>

    <div v-if="busy">
      <fingerprint-spinner :animation-duration="1500" :size="300" color="#293a93" class="align-middle" />
    </div>
    <table v-else class="table table-striped fixed-table">
      <thead>
        <tr>
          <th>Part Number</th>
          <th>Description</th>
          <th>Total On Order</th>
          <th>Classification</th>
          <th>Available</th>
          <th>Reorder Level</th>
          <th>Order Up to</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="data in newItems">
          <tr>
            <td>
              <router-link target="_blank" :to="{ name: 'Qty Calculations', query: { partNum: data.part } }">
                <span :style="`background: ${partNumBackground(data)};`">
                  {{ data.part }}
                </span>
              </router-link>
            </td>
            <td>
              {{ data.description }}

              <div style="color:darkgreen;" v-if='data.partordernote'>
                  PURCHASE NOTE: {{ data.partordernote }}
                </div>
                <div style="color:darkorange;" v-if='data.engineeringnote && data.engineeringnote != data.partordernote'>
                  ENG NOTE: {{ data.engineeringnote }}
                </div>

                <table v-if="!!data.orderedAs.length > 0" class="table table-striped inline-table">
                  <tr>
                    <th>Num</th>
                    <th>On Order</th>
                    <th>PO Nums</th>
                    <th>Available</th>
                    <th>In MOs</th>
                  </tr>
                  <tr v-for="row in data.orderedAs" :key="row.num">
                    <td>
                      <router-link target="_blank" :to="{ name: 'Qty Calculations', query: { partNum: row.num } }">
                        {{ row.num }}
                      </router-link>
                    </td>
                    <td>
                      <span :style="`background: ${ row.onOrder > 0 ? 'lightgreen' : 'inherit' };`">
                        {{ formatNum(row.onOrder) }} {{ row.uom }}
                      </span>
                    </td>
                    <td>
                      <span v-if="row.poNums">
                        <router-link
                          v-for="poNum in row.poNums.split(',')" :key="poNum"
                          :to="{ name: 'PO Report', query: { num: poNum } }" target="_blank"
                        >
                          {{ poNum }}
                        </router-link>
                      </span>
                    </td>
                    <td>
                      <span v-if="row.typeId == 10">
                        {{ formatNum(row.available) }} {{ row.uom }}
                      </span>
                      <span v-else>
                        {{ row.partType }}
                      </span>
                    </td>
                    <td>
                      {{ formatNum(row.inMos) }} {{ row.uom }}
                    </td>
                  </tr>
                </table>
            </td>
            <td>
              <span :style="`background: ${ data.totalonorder > 0 ? 'lightgreen' : 'inherit' };`">
                {{ formatNum(data.totalonorder) }}
              </span>
              <span v-if="data.totalonorder > 0">
                <br/>
                (<router-link
                  v-for="poNum in data.poNums.split(',')" :key="poNum"
                  :to="{ name: 'PO Report', query: { num: poNum } }" target="_blank"
                >
                  {{ poNum }}
                </router-link>)
              </span>
            </td>
            <td>
              {{ data.classification }}
            </td>
            <td>
              <span :style="`background: ${ (data.reorderpoint * .1) > data.available > 0 ? 'lightcoral' : 'inherit' };`">
                {{ formatNum(data.available) }} {{ data.uomcode }}
              </span>
            </td>
            <td>
              {{ formatNum(data.reorderpoint) }}
            </td>
            <td>
              {{ formatNum(data.orderuptolevel) }}
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import NiceDate from '../utility/NiceDate';
import { FingerprintSpinner } from 'epic-spinners';

const BASE_API = process.env.VUE_API_ROOT;

export default {
  components: {
    NiceDate,
    FingerprintSpinner,
  },
  data() {
    return {
      showPos: true,
      filter: '',
      generatedAt: '',

      busy: false,
      columns: [
        { key: 'part', label: 'Part Number', sortable: true },
        { key: 'description', sortable: true },
        { key: 'totalonorder', label: 'Total On Order', sortable: true },
        { key: 'classification', sortable: true },
        { key: 'available', sortable: true },
        { key: 'reorderpoint', label: 'Reorder Level', sortable: true },
        { key: 'orderuptolevel', label: 'Order Up To', sortable: true }
      ],
      items: [],
      newItems: [],

      timer: undefined,
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    getData() {
      this.busy = true;
      axios.get(`${BASE_API}/fishbowl/reports/buy-build-report?buy_mode=true&show_pos=${this.showPos}`).then(response => {
        response.data.forEach(item => {
          item._showDetails = true;
          item.orderedAs = [];
          if (!!item.orderedAsThree) {
            item.orderedAs.push({
              num: item.orderedAsThree,
              onOrder: item.orderedAsThreeOnOrder,
              uom: item.orderedAsThreeUom,
              poNums: item.orderedAsThreePoNums,
              typeId: item.orderedAsThreePartTypeId,
              partType: item.orderedAsThreePartType,
              available: item.orderedAsThreeQtyOnHand,
              inMos: item.orderedAsThreeInMos,
              _cellVariants: {
                onOrder: item.orderedAsThreeOnOrder > 0 ? 'success' : '',
              }
            });
          }
          if (!!item.orderedAsTwo) {
            item.orderedAs.push({
              num: item.orderedAsTwo,
              onOrder: item.orderedAsTwoOnOrder,
              uom: item.orderedAsTwoUom,
              poNums: item.orderedAsTwoPoNums,
              typeId: item.orderedAsTwoPartTypeId,
              partType: item.orderedAsTwoPartType,
              available: item.orderedAsTwoQtyOnHand,
              inMos: item.orderedAsTwoInMos,
              _cellVariants: {
                onOrder: item.orderedAsTwoOnOrder > 0 ? 'success' : '',
              }
            });
          }
          if (!!item.orderedAsOne) {
            item.orderedAs.push({
              num: item.orderedAsOne,
              onOrder: item.orderedAsOneOnOrder,
              uom: item.orderedAsOneUom,
              poNums: item.orderedAsOnePoNums,
              typeId: item.orderedAsOnePartTypeId,
              partType: item.orderedAsOnePartType,
              available: item.orderedAsOneQtyOnHand,
              inMos: item.orderedAsOneInMos,
              _cellVariants: {
                onOrder: item.orderedAsOneOnOrder > 0 ? 'success' : '',
              }
            });
          }
        })
        this.items = response.data;
        this.newItems = this.items;
        this.generatedAt = moment().format("YYYY-MM-DD hh:mm")
        this.unbusy()
      }).catch(error => {
        console.error(error);
      })
    },
    unbusy() {
      this.busy = false;
    },
    formatNum(num) {
      if (typeof (num) == 'undefined') return 0;
      // rounds to whole number and formats to ###,###,###
      return (+Math.round(Number(num), 2)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    partNumBackground(part) {
      switch(part.amparttype) {
        case 'Purchased': return 'yellow'
        case 'Trying to Die': return 'darkgrey'
        default: return 'inherit'
      }
    },
    filterItems(timeout = 400) {
      if (!!this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.newItems = this.items.filter(item => {
          return JSON.stringify(item).toLowerCase().indexOf(this.filter.toLowerCase()) > -1
        })
      }, timeout);
    }
  },
}
</script>



// WEBPACK FOOTER //
// src/components/purchasing/BuySheet.vue